import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import debounce from "lodash/debounce";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import SearchIcon from "@material-ui/icons/Search";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import InputAdornment from "@material-ui/core/InputAdornment";
import A_SimpleDialog from "../../../Atoms/Feedback/Dialogs/A_SimpleDialog.jsx";
import A_Button from "../../../Atoms/Inputs/Buttons/A_Button";
import A_TextField from "../../../Atoms/Inputs/TextFields/A_TextField";
import A_SimpleSelect from "../../../Atoms/Inputs/Selects/A_SimpleSelect";
import M_DataStateRenderer from "../../../Molecules/Common/DataStateRenderer/M_DataStateRenderer.jsx";
import LoaderSkeleton from "../../../Molecules/Common/LoaderSkeleton";
import TablePaginationFooter from "../DrawdownApproval/TablePaginationFooter";
import StatusTag from "./StatusTag";
import { loanStatusTone, formatMoney } from "./AssociatedLoansPreview";
import {
  api,
  buildAssociatedLoansQueryParams,
  PROFESSIONAL_DETAILS_LOAN_TEMPLATE_ID,
} from "./ProfessionalContacts.helper";
import {
  LOAN_DETAIL_ROLE_KEYS,
  LOAN_DETAIL_ROLE_LABELS,
} from "../../../../../domain/professionalContacts/professionalContacts.types";
import useStyles from "./ProfessionalContacts.styles";

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

const COLUMNS = [
  { key: "loanName", label: "Loan Name" },
  { key: "borrowerName", label: "Borrower Name" },
  { key: "facilityLimit", label: "Facility Limit", numeric: true },
  { key: "status", label: "Status" },
  { key: "stage", label: "Stage" },
];

const ROLE_OPTIONS = Object.entries(LOAN_DETAIL_ROLE_KEYS).map(
  ([partyRole, roleKey]) => ({
    value: partyRole,
    label: LOAN_DETAIL_ROLE_LABELS[roleKey],
  })
);

const AssociatedLoansDialog = ({ firmId, firmName, onClose }) => {
  const classes = useStyles();
  const [searchText, setSearchText] = useState("");
  const [stage, setStage] = useState("");
  const [status, setStatus] = useState("");
  const [role, setRole] = useState("");
  const [sort, setSort] = useState("loanName");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [knownStages, setKnownStages] = useState(() => new Set());
  const [knownStatuses, setKnownStatuses] = useState(() => new Set());
  const [resetToken, setResetToken] = useState(0);
  const requestSeq = useRef(0);

  const load = useCallback(
    async (params) => {
      const seq = ++requestSeq.current;
      setLoading(true);
      setError(null);
      try {
        const result = await api.listAssociatedLoans(
          firmId,
          buildAssociatedLoansQueryParams(params)
        );
        if (seq !== requestSeq.current) return;
        const rows = result?.items || [];
        setItems(rows);
        setTotal(result?.total ?? 0);
        setKnownStages((prev) => {
          const next = new Set(prev);
          rows.forEach((r) => r.stage && next.add(r.stage));
          return next;
        });
        setKnownStatuses((prev) => {
          const next = new Set(prev);
          rows.forEach((r) => r.status && next.add(r.status));
          return next;
        });
      } catch (err) {
        if (seq === requestSeq.current) setError(err);
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    },
    [firmId]
  );

  const debouncedLoad = useMemo(() => debounce(load, 350), [load]);

  useEffect(() => () => debouncedLoad.cancel(), [debouncedLoad]);

  useEffect(() => {
    load({ page: 1, pageSize: rowsPerPage, sort, sortDir });
  }, []);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchText(value);
    setPage(0);
    debouncedLoad({
      q: value,
      stage,
      status,
      role,
      sort,
      sortDir,
      page: 1,
      pageSize: rowsPerPage,
    });
  };

  const handleStageChange = (value) => {
    setStage(value);
    setPage(0);
    load({
      q: searchText,
      stage: value,
      status,
      role,
      sort,
      sortDir,
      page: 1,
      pageSize: rowsPerPage,
    });
  };

  const handleStatusChange = (value) => {
    setStatus(value);
    setPage(0);
    load({
      q: searchText,
      stage,
      status: value,
      role,
      sort,
      sortDir,
      page: 1,
      pageSize: rowsPerPage,
    });
  };

  const handleRoleChange = (value) => {
    setRole(value);
    setPage(0);
    load({
      q: searchText,
      stage,
      status,
      role: value,
      sort,
      sortDir,
      page: 1,
      pageSize: rowsPerPage,
    });
  };

  const handleSort = (key) => {
    const nextDir = sort === key ? (sortDir === "asc" ? "desc" : "asc") : "asc";
    setSort(key);
    setSortDir(nextDir);
    setPage(0);
    load({
      q: searchText,
      stage,
      status,
      role,
      sort: key,
      sortDir: nextDir,
      page: 1,
      pageSize: rowsPerPage,
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    load({
      q: searchText,
      stage,
      status,
      role,
      sort,
      sortDir,
      page: newPage + 1,
      pageSize: rowsPerPage,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
    load({
      q: searchText,
      stage,
      status,
      role,
      sort,
      sortDir,
      page: 1,
      pageSize: newSize,
    });
  };

  const clearFilters = () => {
    setSearchText("");
    setStage("");
    setStatus("");
    setRole("");
    setPage(0);
    setResetToken((token) => token + 1);
    load({ page: 1, pageSize: rowsPerPage, sort, sortDir });
  };

  const hasActiveFilters = Boolean(searchText || stage || status || role);
  const stageOptions = [
    { value: "", label: "All Stages" },
    ...Array.from(knownStages)
      .sort()
      .map((s) => ({ value: s, label: s })),
  ];
  const statusOptions = [
    { value: "", label: "All Statuses" },
    ...Array.from(knownStatuses)
      .sort()
      .map((s) => ({ value: s, label: s })),
  ];
  const roleOptions = [{ value: "", label: "All Roles" }, ...ROLE_OPTIONS];

  return (
    <A_SimpleDialog
      open
      onClose={onClose}
      title={<span className={classes.dialogTitleText}>Associated Loans</span>}
      maxWidth="lg"
      fullWidth
      dialogContent={
        <div className={classes.dialogPaper}>
          {firmName && <div className={classes.dialogSubtitle}>{firmName}</div>}

          <div className={classes.toolbar}>
            <div className={classes.filtersRow}>
              <A_SimpleSelect
                id="associated-loans-stage"
                label="Stage"
                className={classes.field}
                size="small"
                defaultValue={stage}
                options={stageOptions}
                optionValue="value"
                optionLabel="label"
                onChange={handleStageChange}
                style={{ margin: 0, minWidth: 130, flex: "1 1 130px" }}
              />
              <A_SimpleSelect
                id="associated-loans-status"
                label="Status"
                className={classes.field}
                size="small"
                defaultValue={status}
                options={statusOptions}
                optionValue="value"
                optionLabel="label"
                onChange={handleStatusChange}
                style={{ margin: 0, minWidth: 130, flex: "1 1 130px" }}
              />
              <A_SimpleSelect
                id="associated-loans-role"
                label="Role"
                className={classes.field}
                size="small"
                defaultValue={role}
                options={roleOptions}
                optionValue="value"
                optionLabel="label"
                onChange={handleRoleChange}
                style={{ margin: 0, minWidth: 130, flex: "1 1 130px" }}
              />
              {hasActiveFilters && (
                <button
                  type="button"
                  className={classes.linkAction}
                  onClick={clearFilters}
                >
                  Clear Filters
                </button>
              )}
            </div>

            <div className={classes.searchBlock}>
              <A_TextField
                key={resetToken}
                id="associated-loans-search"
                className={classes.searchInput}
                label="Search"
                defaultValue={searchText}
                onChange={handleSearchChange}
                margin="none"
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        style={{
                          fontSize: "1rem",
                          color: "var(--color-text-secondary)",
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              <span className={classes.resultCount}>
                {loading ? "Searching…" : `${items.length} of ${total}`}
              </span>
            </div>
          </div>

          <div className={clsx(classes.tableContainer, classes.tableSpaced)}>
            <M_DataStateRenderer
              loading={loading && items.length === 0}
              error={error}
              data={items}
              isEmpty={!loading && !error && items.length === 0}
              empty={
                <div className={classes.emptyState}>
                  {hasActiveFilters
                    ? "No associated loans match your search or filters."
                    : "No associated loans."}
                </div>
              }
              skeleton={<LoaderSkeleton />}
            >
              <Table
                size="small"
                className={clsx(classes.table, classes.tableWide)}
              >
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    {COLUMNS.map((col) => (
                      <TableCell
                        key={col.key}
                        align={col.numeric ? "right" : "left"}
                        sortDirection={sort === col.key ? sortDir : false}
                      >
                        <TableSortLabel
                          className={classes.sortLabel}
                          active={sort === col.key}
                          direction={sort === col.key ? sortDir : "asc"}
                          onClick={() => handleSort(col.key)}
                        >
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell>Role</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody className={classes.tableBody}>
                  {items.map((loan) => (
                    <TableRow key={`${loan.loanId}-${loan.role}`} hover>
                      <TableCell>
                        <a
                          href={`/loan/${loan.loanId}/${PROFESSIONAL_DETAILS_LOAN_TEMPLATE_ID}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={classes.rowLink}
                        >
                          {loan.loanName}
                          <OpenInNewIcon className={classes.rowLinkIcon} />
                        </a>
                        <div className={classes.subtle}>
                          Ref {loan.loanNumber}
                        </div>
                      </TableCell>
                      <TableCell>{loan.borrowerName}</TableCell>
                      <TableCell align="right" className={classes.cellNowrap}>
                        {formatMoney(loan.facilityLimit)}
                      </TableCell>
                      <TableCell>
                        <StatusTag tone={loanStatusTone(loan.status)}>
                          {loan.status || "-"}
                        </StatusTag>
                      </TableCell>
                      <TableCell>{loan.stage || "-"}</TableCell>
                      <TableCell>{loan.roleLabel}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </M_DataStateRenderer>
          </div>

          <TablePaginationFooter
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </div>
      }
      dialogActions={
        <A_Button label="Close" premiumVariant="secondary" onClick={onClose} />
      }
    />
  );
};

AssociatedLoansDialog.propTypes = {
  firmId: PropTypes.string.isRequired,
  firmName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default AssociatedLoansDialog;
